import { find } from "lodash";
import template from "./form-agency-picker.html";
import "./form-agency-picker.less";

export const FORM_VIEW_MODES = Object.freeze({
	LIST: "list",
	COLLAPSED: "collapsed"
});

//formAgencyPicker
export function controller($logger) {
	const ctrl = this;

	ctrl.$onInit = onInit;
	ctrl.$onChanges = onChanges;
	ctrl.agencies = [];
	ctrl.isAgencyListExpanded = false;
	ctrl.onAgencyExpandedChanged = onAgencyExpandedChanged;
	ctrl.selectAgency = selectAgency;
	ctrl.selectForm = selectForm;
	ctrl.setFormBox = setFormBox;
	ctrl.setFormDisplay = setFormDisplay;
	ctrl.handleFormValueChanged = handleFormValueChanged;

	function onInit() {
		ctrl.viewMode = normalizeViewMode(ctrl.viewMode);
		handleBindingsChange();
	}

	function onChanges(changes) {
		if(changes.forms) {
			handleBindingsChange();
		}
		else if(changes.selected) {
			applySelectedForm(ctrl.selected);
		}
	}

	function handleBindingsChange() {
		const selectedAgencyId = ctrl.selected?.Agency?.Id ?? ctrl.selectedAgency?.id;

		ctrl.agencies = buildAgencies(ctrl.forms || []);
		ctrl.selectedAgency = find(ctrl.agencies, { id: selectedAgencyId });
		applySelectedForm(ctrl.selected);
	}

	function applySelectedForm(selectedForm) {
		if(selectedForm?.Agency?.Id) {
			ctrl.selectedAgency = find(ctrl.agencies, { id: selectedForm.Agency.Id });
		}

		ctrl.selectedFormId = ctrl.selectedAgency ? selectedForm?.Id : undefined;
		ctrl.selectedFormModel = ctrl.selectedFormId?.toString();
		setFormOptions();
	}

	function onAgencyExpandedChanged(event) {
		ctrl.isAgencyListExpanded = event.isExpanded;
	}

	function selectAgency(agency) {
		if(!agency) {
			return;
		}

		const isSelectedAgency = agency.id === ctrl.selected?.Agency?.Id;

		ctrl.selectedAgency = agency;
		ctrl.selectedFormId = isSelectedAgency ? ctrl.selected.Id : undefined;
		ctrl.selectedFormModel = ctrl.selectedFormId?.toString();

		if(agency.forms.length === 1 && !isSelectedAgency) {
			selectForm(agency.forms[0]);
			setFormOptions();
			return;
		}

		setFormOptions();

		if(isSelectedAgency) {
			return;
		}

		if(ctrl.onAgencySelected) {
			ctrl.onAgencySelected({ $event: { agency: ctrl.selectedAgency } });
		}
	}

	function selectForm(form) {
		if(!form) {
			return;
		}

		ctrl.selectedFormId = form.id;
		ctrl.selectedFormModel = form.id.toString();

		const selectedForm = find(ctrl.forms, { Id: form.id });

		if(ctrl.onFormSelected) {
			ctrl.onFormSelected({ 
				$event: { 
					agency: ctrl.selectedAgency,
					form: selectedForm,
					isFormUnchanged: ctrl.selected?.Id === form.id
				} 
			});
		}
	}

	function setFormBox(box) {
		ctrl.formBox = box;
		setFormOptions();
	}

	function setFormOptions() {
		if(!ctrl.formBox) {
			return;
		}

		ctrl.formBox.clearStore();

		const forms = ctrl.selectedAgency?.forms || [];
		const choices = forms.map((form) => ({
			value: form.id.toString(),
			label: form.displayName
		}));

		ctrl.formBox.setChoices(choices, "value", "label", true);

		if(ctrl.selectedFormId !== undefined) {
			ctrl.formBox.setValueByChoice(ctrl.selectedFormId.toString());
		}
	}

	function setFormDisplay(id) {
		const formId = parseInt(id) || id;
		const form = find(ctrl.selectedAgency?.forms, { id: formId });

		if(!form) {
			$logger.warn("Selected form not found as an option.");
		}

		return form?.displayName;
	}

	function handleFormValueChanged(modelValue) {
		const formId = parseInt(modelValue) || modelValue;
		selectForm(find(ctrl.selectedAgency?.forms, { id: formId }));
	}

	function buildAgencies(forms) {
		const groupedByAgency = forms.reduce((accumulator, form) => {
			const agencyId = form.Agency.Id;
			const existingAgency = accumulator[agencyId] || {
				id: agencyId,
				name: form.Agency.Name,
				initials: form.Agency.Initials,
				code: form.Agency.Code,
				agencyLogo: form.Agency.Logo,
				forms: []
			};

			existingAgency.forms.push({
				id: form.Id,
				name: form.Name,
				code: form.Code,
				displayName: toDisplayCase(form.Name),
				nomenclatureLabel: getNomenclatureLabel(form),
				logo: getFormLogoUrl(form),
				positiveReport: cleanValue(form.nomenclature?.PositiveReport),
				primaryEntity: cleanValue(form.nomenclature?.PrimaryEntity),
				primaryDate: cleanValue(form.nomenclature?.PrimaryDate)
			});

			accumulator[agencyId] = existingAgency;

			return accumulator;
		}, {});

		return Object.values(groupedByAgency)
			.map((agency) => {
				const formsByNomenclature = [...agency.forms].sort((left, right) => {
					return compareText(left.nomenclatureLabel, right.nomenclatureLabel);
				});
				const fallbackLogo = formsByNomenclature.find((form) => hasValue(form.logo))?.logo;
				const formsByName = [...agency.forms].sort((left, right) => {
					return compareText(left.name, right.name);
				});

				return {
					id: agency.id,
					name: agency.name,
					initials: agency.initials,
					code: agency.code,
					logo: hasValue(agency.agencyLogo) ? agency.agencyLogo : fallbackLogo,
					forms: formsByName
				};
			})
			.sort((left, right) => {
				return compareText(left.name, right.name);
			});
	}

	function getFormLogoUrl(form) {
		if(form.nomenclature?.LogoUrl) {
			return cleanValue(form.nomenclature.LogoUrl);
		}
		else if(form.Agency.Logo) {
			return cleanValue(form.Agency.Logo);
		}
		
		const logoEntry = form.Nomenclature?.find((entry) => entry.Code === "FORM.LOGO");
		return cleanValue(logoEntry?.Text);
	}

	function getNomenclatureLabel(form) {
		if(typeof form.nomenclature === "string") {
			return form.nomenclature;
		}

		return form.nomenclature?.FormName || form.nomenclature?.PositiveReport || form.Name || "";
	}

	function compareText(left, right) {
		return (left || "").localeCompare(right || "", undefined, { sensitivity: "base" });
	}

	function hasValue(value) {
		return typeof value === "string" && value.trim().length > 0;
	}

	function cleanValue(value) {
		if(typeof value !== "string") {
			return "";
		}

		return value.trim();
	}

	function toDisplayCase(value) {
		const text = cleanValue(value);

		return text;
	}

	function normalizeViewMode(viewMode) {
		return viewMode === FORM_VIEW_MODES.COLLAPSED
			? FORM_VIEW_MODES.COLLAPSED
			: FORM_VIEW_MODES.LIST;
	}
}

export default angular.module("vesl.components.form-agency-picker", []).component("formAgencyPicker", {
	controller,
	template,
	bindings: {
		onFormSelected: "&",
		onAgencySelected: "&",
		forms: "<",
		selected: "<?",
		viewMode: "@?",
		disabledFormId: "<"
	}
}).name;
